import React, { useState } from "react";
import styled from "styled-components";
import { vendorOpenBadge } from "../utils/vendorOpenState";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom";
import { FaImage } from "react-icons/fa";
import DeliveryBadge from "./DeliveryBadge";
import useCoverage from "../redux/useCoverage";
import OfficialStoreBadge from "./OfficialStoreBadge";
import CrawlLink from "./CrawlLink";
import { shopPath } from "../utils/seoPaths";
import { getBrandTheme } from "../utils/brandTheme";
import { vendorTaglineOf } from "../utils/vendorTagline";
import {
  getOptimizedUrl,
  getResponsiveCloudinarySrcSet,
} from "../utils/cloudinaryOptimize";

const getCarouselImageUrl = (imageUrl, isLogo = false) => {
  if (!imageUrl) return null;
  if (isLogo) {
    return getOptimizedUrl(imageUrl, "brandCarouselLogo");
  }
  return getOptimizedUrl(imageUrl, "brandCarousel");
};

const getCarouselImageSrcSet = (imageUrl, isLogo = false) => {
  if (!imageUrl) return "";
  return getResponsiveCloudinarySrcSet(
    imageUrl,
    isLogo ? [260, 400, 512] : [320, 480, 640, 960, 1280],
    isLogo
      ? {
          aspectRatio: 1,
          crop: "pad",
          background: "white",
          quality: "auto:best",
          format: "auto",
        }
      : {
          aspectRatio: 16 / 9,
          crop: "fill",
          gravity: "auto",
          quality: "auto:best",
          format: "auto",
        },
  );
};

const BrandCard = ({ brand, onClick }) => {
  const openBadge = vendorOpenBadge(brand);
  const isOfficialStore = brand.verifiedVendorBadge !== false;
  const history = useHistory();
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // AUTHORITATIVE deliverability from the backend (same verdict as the
  // order-create gate; cached per vendor+location, so a carousel re-render
  // never refetches). The badge falls back to its client "~" estimate only
  // if this check fails.
  const coverage = useCoverage({
    vendorId: brand?._id,
    vendorType: "brand",
    enabled: Boolean(brand?._id),
  });

  // The storefront's canonical address. The picture and the name link to it
  // so search engines can reach the shop; a tap still runs handleClick.
  const to = shopPath("brand", brand);

  const handleClick = () => {
    if (onClick) onClick(brand);
    else history.push(to);
  };

  // Prefer banner (wide, designed for carousel). Fall back to imageUrl (logo)
  // so the card is never empty — logo mode uses contain + padding so it
  // doesn't get cropped or stretched inside the image zone.
  const hasBanner = !!brand.bannerUrl;
  const hasImage = brand.bannerUrl || brand.imageUrl;
  const imageUrl = hasImage
    ? getCarouselImageUrl(brand.bannerUrl || brand.imageUrl, !hasBanner)
    : null;
  const imageSrcSet = hasImage
    ? getCarouselImageSrcSet(brand.bannerUrl || brand.imageUrl, !hasBanner)
    : "";

  const industry = brand.industryCategory || brand.industryCategoryTag || null;
  // A live promotion's banner text wins; otherwise the brand's own tagline.
  const tagline = brand.promotion?.promoBannerText || vendorTaglineOf(brand);
  const theme = getBrandTheme(brand);

  return (
    <Card
      as={motion.div}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
      onClick={handleClick}
    >
      {/* ── Image zone ── */}
      <CrawlLink to={to} onNavigate={handleClick} style={{ display: "block" }}>
        <ImageZone $logoOnly={!hasBanner}>
          {imageUrl && !imageError ? (
            <>
              <BannerImage
                src={imageUrl}
                srcSet={imageSrcSet || undefined}
                sizes="(max-width: 480px) 260px, 320px"
                alt={brand.name}
                loading="lazy"
                decoding="async"
                onError={() => setImageError(true)}
                onLoad={() => setImageLoaded(true)}
                $loaded={imageLoaded}
                $logoOnly={!hasBanner}
              />
              {!imageLoaded && <Shimmer />}
            </>
          ) : (
            <NoImageFill>
              <FaImage size={32} color="rgba(255,255,255,0.3)" />
            </NoImageFill>
          )}

          {/* Subtle scrim only at very bottom of banner images — hidden for logo-only cards */}
          <BottomScrim $logoOnly={!hasBanner} />

          {/* Hover CTA slides up from inside the image zone */}
          <HoverCTA>
            <CTAInner $color={theme.secondary} $text={theme.onSecondary}>
              <span>Explore Products</span>
              <CTAArrow $color={theme.primary}>→</CTAArrow>
            </CTAInner>
          </HoverCTA>
        </ImageZone>
      </CrawlLink>

      {/* ── Info strip ── */}
      <InfoStrip>
        {(industry || isOfficialStore) && (
          <IndustryRow>
            {industry && <IndustryLabel>{industry}</IndustryLabel>}
            {isOfficialStore && (
              <OfficialStoreBadge size="small" />
            )}
          </IndustryRow>
        )}
        <BrandNameText>
          <CrawlLink to={to} onNavigate={handleClick}>
            {brand.name}
          </CrawlLink>
        </BrandNameText>
        {tagline && <TaglineText>{tagline}</TaglineText>}
        <InfoFooter>
          <DeliveryBadge
            vendor={brand}
            coverage={coverage}
            checkOutlets
            size="small"
          />
          {/* Open state, from the server. The web brand list showed none at
              all, so a shopper had to open a storefront to discover it was
              shut. Null = nothing published, and we draw nothing rather than
              imply the shop is trading. */}
          {openBadge ? (
            <OpenBadge $tone={openBadge.tone}>{openBadge.label}</OpenBadge>
          ) : null}
        </InfoFooter>
      </InfoStrip>
    </Card>
  );
};

export default BrandCard;

/* ─────────────────────────────────────────────
   Styled Components
───────────────────────────────────────────── */

const Card = styled.div`
  flex: 0 0 auto;
  width: 320px;
  /* A column, so the info strip fills the rail's height and the location and
     open pills sit at the bottom of every card, however long the tagline. */
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  box-shadow:
    0 2px 12px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.06);
  transition:
    box-shadow 0.35s ease,
    transform 0.35s ease;
  scroll-snap-align: start;

  &:hover {
    box-shadow:
      0 16px 48px rgba(0, 0, 0, 0.14),
      0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-6px);
  }

  @media (max-width: 480px) {
    width: 260px;
  }
`;

const ImageZone = styled.div`
  position: relative;
  width: 100%;
  /* Logo-only: square aspect ratio so a 400×400 logo fills the zone naturally.
     Banner: 16:9 aspect ratio — matches wide landscape banners as uploaded. */
  height: auto;
  aspect-ratio: ${(p) => (p.$logoOnly ? "1 / 1" : "16 / 9")};
  overflow: hidden;
  background: ${(p) => (p.$logoOnly ? "#ffffff" : "#1a1a2e")};
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  /* Banner: fill the zone. Logo: contain + pad so it's never cropped/stretched */
  object-fit: ${(p) => (p.$logoOnly ? "contain" : "cover")};
  object-position: center;
  padding: 0;
  display: block;
  opacity: ${(p) => (p.$loaded ? 1 : 0)};
  transition:
    opacity 0.4s ease,
    transform 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  ${Card}:hover & {
    transform: ${(p) => (p.$logoOnly ? "scale(1.04)" : "scale(1.06)")};
  }
`;

const Shimmer = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #e8e8e8 25%, #f4f4f4 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const NoImageFill = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #2d3561 100%);
`;

/* Scrim: only bottom 35% of image, very subtle */
const BottomScrim = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 35%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.32) 0%, transparent 100%);
  pointer-events: none;
  z-index: 1;
  display: ${(p) => (p.$logoOnly ? "none" : "block")};
`;

const IndustryLabel = styled.div`
  min-width: 0;
  flex: 1;
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: 0;
  text-transform: uppercase;
  color: #667085;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IndustryRow = styled.div`
  min-height: 18px;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

/* Full-width CTA that slides up on hover */
const HoverCTA = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 4;
  transform: translateY(100%);
  transition: transform 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  ${Card}:hover & {
    transform: translateY(0);
  }
`;

const CTAInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${(p) => p.$color || "rgba(255,255,255,0.96)"};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  font-size: 0.82rem;
  font-weight: 700;
  color: ${(p) => p.$text || "#fff"};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const CTAArrow = styled.span`
  display: inline-block;
  color: ${(p) => p.$color || "currentColor"};
  font-size: 1.05em;
  transition: transform 0.22s ease;
  ${CTAInner}:hover & {
    transform: translateX(4px);
  }
`;

/* ── Info strip below the image ── */
const InfoStrip = styled.div`
  flex: 1;
  padding: 14px 16px 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 3px;
  border-top: 1px solid #ebebeb;
  box-shadow: inset 0 3px 8px rgba(0, 0, 0, 0.08);
`;

const BrandNameText = styled.h3`
  margin: 0;
  font-size: 0.97rem;
  font-weight: 700;
  color: #111;
  letter-spacing: -0.01em;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Two lines before the ellipsis: a tagline is a sentence, and one line cut
   most of them off mid-word on a 320px card. */
const TaglineText = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: #666;
  line-height: 1.4;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const InfoFooter = styled.div`
  margin-top: auto;
  padding-top: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  & > * {
    margin-top: 0;
  }
`;

/* Shimmer placeholder for skeleton (carousel uses this) */
const OPEN_BADGE_COLOURS = {
  open: "#10B981",
  // Closed now, takes orders for later: calm blue, not the red of "Closed".
  scheduled: "#1D4ED8",
  closed: "#EF4444",
  unavailable: "#6B7280",
};

const OpenBadge = styled.span`
  padding: 2px 8px;
  border-radius: 99px;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  color: #fff;
  background: ${({ $tone }) =>
    OPEN_BADGE_COLOURS[$tone] || OPEN_BADGE_COLOURS.unavailable};
`;

const BrandCardSkeleton = styled.div`
  flex: 0 0 auto;
  width: 320px;
  height: 280px;
  border-radius: 18px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f4f4f4 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: shimmerSkel 1.4s infinite;
  @keyframes shimmerSkel {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
  @media (max-width: 480px) {
    width: 260px;
    height: 230px;
  }
`;

export { BrandCardSkeleton };
